import { forwardRef } from "react"
import styled from "styled-components"
import Color from "../../constants/Color"
import Spacer from "../atoms/Spacer"
import WhatWeDoImage from "./WhatWeDoImage"
import * as Animation from "../../functionality/Animation"
import * as Layout from "../../functionality/Layout"

interface Props {
    widthInPx: number
    image: string
    imageAlt: string
    title: string
    detail: string
    shouldFadeImageEdge?: boolean
    imageSaturation?: number
    tween: number
}

const titleTweenTransfomWhenHorizontal: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0,
    endControl: 0.5,
}

const titleTweenTransformWhenVertical: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.6,
    endControl: 1,
}

const detailTweenTransfomWhenHorizontal: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.5,
    endControl: 1,
}

const detailTweenTransfomWhenVertical: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.8,
    endControl: 1,
}

const ImageTitleText = forwardRef<HTMLDivElement, Props>(({widthInPx, image, imageAlt, title, detail, shouldFadeImageEdge, imageSaturation, tween}, ref) => {

    const isVertical = Layout.WhatWeDo.Section.isVerticalForWidthInPx(widthInPx)
    const titleFontSizeInPx = Animation.calculateValue(Layout.WhatWeDo.Section.titleFontSizeValues, widthInPx)
    const detailFontSizeInPx = Animation.calculateValue(Layout.WhatWeDo.Section.detailFontSizeValues, widthInPx)

    const marginInPx = Animation.calculateValue(Layout.WhatWeDo.Section.marginValues, widthInPx)

    const imageSize = isVertical ? {
                                        widthInPx: Layout.WhatWeDo.Section.imageWidthForHeight(Layout.WhatWeDo.Section.textHeightWhenHorizontalInPx),
                                        heightInPx: Layout.WhatWeDo.Section.textHeightWhenHorizontalInPx,
                                   }
                                 : {
                                        widthInPx: ((widthInPx - marginInPx) * 0.34),
                                        heightInPx: Layout.WhatWeDo.Section.imageHeightForWidth((widthInPx - marginInPx) * 0.34),
                                    }
    const textWidthInPx = isVertical ? widthInPx
                                     : (widthInPx - imageSize.widthInPx - marginInPx)

    const titleTween = isVertical ? Animation.calculateValue(titleTweenTransformWhenVertical, tween)
                                  : Animation.calculateValue(titleTweenTransfomWhenHorizontal, tween)
    const titleToUse = Animation.typeOut(title, titleTween)

    const detailTween = isVertical ? Animation.calculateValue(detailTweenTransfomWhenVertical, tween)
                                   : Animation.calculateValue(detailTweenTransfomWhenHorizontal, tween)
    // const detailToUse = Animation.typeOut(detail, detailTween)

    const easeInOutTween = Animation.easeInOut(tween)

    const imageOffset = {
        leftInPx: isVertical ? 0 : (Layout.WhatWeDo.Section.maxImageOffsetInPx * (1 - easeInOutTween)),
        topInPx: isVertical ? (Layout.WhatWeDo.Section.maxImageOffsetInPx * (1 - easeInOutTween)) : 0,
    }

    return <Wrapper ref={ref} widthInPx={widthInPx} columnGapInPx={marginInPx} flexDirection={isVertical ? "column" : "row"}>
        <WhatWeDoImage src={image}
                       alt={imageAlt}
                       widthInPx={imageSize.widthInPx}
                       heightInPx={imageSize.heightInPx}
                       saturation={imageSaturation}
                       shouldFadeEdge={shouldFadeImageEdge ?? false}
                       opacity={tween}
                       offset={imageOffset}/>
        {isVertical && <Spacer heightInPx={Layout.WhatWeDo.Section.verticalImageTextSpacerHeightInPx}/>}
        <TextContainer widthInPx={textWidthInPx}>
            <Title fontSizeInPx={titleFontSizeInPx} textAlign={isVertical ? "center" : "left"}>{titleToUse}</Title>
            <Spacer heightInPx={10}/>
            <Detail fontSizeInPx={detailFontSizeInPx} opacity={detailTween}>{detail}</Detail>
        </TextContainer>
    </Wrapper>
})

export default ImageTitleText

const Wrapper = styled.div.attrs<{widthInPx: number, columnGapInPx: number, flexDirection: "row" | "column"}>(p => ({
    style: {
        width: p.widthInPx + "px",
        columnGap: p.columnGapInPx + "px",
        flexDirection: p.flexDirection,
    }
}))`
    display: flex;
    align-items: center;
    flex-grow: 0;
    flex-shrink: 0;
`

const TextContainer = styled.div.attrs<{widthInPx: number}>(p => ({
    style: {
        width: p.widthInPx + "px",
    }
}))`
    flex-shrink: 0;
    flex-grow: 0;
`

const Title = styled.div.attrs<{fontSizeInPx: number, textAlign: "left" | "center"}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        height: p.fontSizeInPx + "px",
        textAlign: p.textAlign,
    }
}))`
    color: ${Color.Gold};
    font-weight: 900;
    letter-spacing: 0.08em;
`

const Detail = styled.div.attrs<{fontSizeInPx: number, opacity: number}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        opacity: p.opacity,
    }
}))`
    /* height: ${Layout.WhatWeDo.Section.textHeightWhenHorizontalInPx}px; */
    color: ${Color.Black};
    font-size: 20px;
    line-height: 1.5;

    /* width: 380px; */
`